import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Card } from './ui/Card';
import { Sheet, SheetBody, SheetContent } from './ui/Sheet';
import { RateBar } from './RateBar';
import { KillSwitchDialog } from './KillSwitchDialog';
import { ResumeSendingDialog } from './ResumeSendingDialog';
import { useAuth } from '../lib/auth';
import { SenderStatusCard } from './SenderStatusCard';
import { apiFetch } from '../lib/api';
import { formatNumber, formatPercent } from '../lib/format';
import { cn } from '../lib/utils';
import type { HealthSummary } from '../types/api';

function useHealthSummary() {
  return useQuery({
    queryKey: ['health-summary'],
    queryFn: () => apiFetch<HealthSummary>('/health-summary'),
    refetchInterval: 30_000,
  });
}

/**
 * `day` is the 0-based plan day offset; people count from 1. On the plan's
 * last day the limit no longer rises, so the warm-up reads as complete.
 */
function warmupProgress(warmup: NonNullable<HealthSummary['warmup']>) {
  const complete = warmup.day >= warmup.totalDays - 1;
  const label = complete ? 'complete' : `day ${warmup.day + 1} of ${warmup.totalDays}`;
  return { complete, label };
}

function WarmupCard({ data }: { data: HealthSummary }) {
  if (!data.warmup) return null;
  const { day, limitToday, schedule } = data.warmup;
  const { complete, label } = warmupProgress(data.warmup);
  const max = schedule.length > 0 ? Math.max(...schedule.map((s) => s.limit)) : limitToday;
  return (
    <Card className="p-4 flex flex-col gap-2">
      <div className="flex text-sm">
        <span className="font-semibold">Warm-up</span>
        <span className="ml-auto text-muted">{label}</span>
      </div>
      <div
        className="flex gap-[3px] items-end h-10"
        role="img"
        aria-label={`Warm-up plan, ${label}, today at most ${formatNumber(limitToday)} emails`}
      >
        {schedule.map((s) => (
          <div
            key={s.day}
            className={
              s.day <= day ? 'flex-grow bg-accent rounded-sm' : 'flex-grow bg-[#D4D5F7] rounded-sm'
            }
            style={{
              height: `${Math.max(8, (s.limit / max) * 100)}%`,
              outline: s.day === day ? '2px solid #18181B' : undefined,
              outlineOffset: s.day === day ? '1px' : undefined,
            }}
          />
        ))}
      </div>
      <div className="text-xs text-muted">
        {complete
          ? `At most ${formatNumber(limitToday)} emails a day`
          : `Today at most ${formatNumber(limitToday)} emails · increases daily`}
      </div>
    </Card>
  );
}

function StatusList({ data }: { data: HealthSummary }) {
  const sesOk = data.ses.mode !== 'unknown';
  return (
    <Card className="p-4 flex flex-col gap-2 text-sm">
      <div className="flex">
        <span>SES connection</span>
        <span className={sesOk ? 'ml-auto text-success-text' : 'ml-auto text-danger-text'}>
          {sesOk ? `✓ ${data.ses.mode === 'fake' ? 'test' : 'ok'}` : '✗ unknown'}
        </span>
      </div>
      <div className="flex">
        <span>Unclear messages</span>
        <span className="ml-auto font-mono">{formatNumber(data.unknownMessages)}</span>
      </div>
      <div className="flex">
        <span>Unsubscribe log</span>
        <span className="ml-auto text-success-text">✓ intact</span>
      </div>
      <div className="flex">
        <span>In queue right now</span>
        <span className="ml-auto font-mono">
          {formatNumber(data.queue.queued + data.queue.retryable)}
        </span>
      </div>
    </Card>
  );
}

type Tone = 'ok' | 'early' | 'warn' | 'bad';

function rateTone(r: HealthSummary['bounce']): Tone {
  if (r.attempted < r.minSample) return 'early';
  if (r.rate >= r.pauseAt) return 'bad';
  if (r.rate >= r.warnAt) return 'warn';
  return 'ok';
}

/** One-line verdict for the strip; the wording follows what the old rail showed. */
function overallStatus(data: HealthSummary): { tone: Tone; text: string } {
  if (data.killSwitch.engaged) return { tone: 'bad', text: 'Kill switch is on. Nothing is sent.' };
  if (data.senderPaused.paused) return { tone: 'bad', text: 'Sending is stopped' };
  const tones = [rateTone(data.bounce), rateTone(data.complaint)];
  if (tones.includes('bad')) return { tone: 'bad', text: 'A rate is at pause level' };
  if (tones.includes('warn')) return { tone: 'warn', text: 'Sending needs attention' };
  // Below the minimum sample auto-pause ignores the rates, so "healthy" would be a claim
  // nothing backs (two simulator bounces in 18 sends read as 11 %).
  if (tones.includes('early')) {
    return {
      tone: 'early',
      text: `Too few sends to judge (${formatNumber(data.bounce.attempted)} of ${formatNumber(data.bounce.minSample)})`,
    };
  }
  return { tone: 'ok', text: 'Sending is healthy' };
}

const DOT: Record<Tone, string> = {
  ok: 'bg-success-bar',
  early: 'bg-[#C9C9CF]',
  warn: 'bg-warning-marker',
  bad: 'bg-danger-text',
};

function Stat({ label, children }: { label: string; children: string }) {
  return (
    <span className="flex items-center gap-1.5 text-muted">
      {label} <b className="font-semibold text-text tabular-nums">{children}</b>
    </span>
  );
}

function ResumeControl() {
  const { isAdmin } = useAuth();
  return isAdmin ? (
    <ResumeSendingDialog compact />
  ) : (
    <span className="text-xs text-muted">Only an administrator can resume sending.</span>
  );
}

function DetailsBody({ data }: { data: HealthSummary }) {
  return (
    <>
      <Card className="p-4 flex flex-col gap-4">
        <div className="flex items-center">
          <h3 className="text-sm font-semibold m-0">Sending health</h3>
          <span className="ml-auto text-xs text-muted">
            {data.bounce.window === '7d' ? '7 days' : data.bounce.window}
          </span>
        </div>
        <RateBar
          label="Bounce"
          rate={data.bounce.rate}
          warnAt={data.bounce.warnAt}
          pauseAt={data.bounce.pauseAt}
          attempted={data.bounce.attempted}
          minSample={data.bounce.minSample}
        />
        <RateBar
          label="Complaints"
          rate={data.complaint.rate}
          warnAt={data.complaint.warnAt}
          pauseAt={data.complaint.pauseAt}
          attempted={data.complaint.attempted}
          minSample={data.complaint.minSample}
        />
        <div className="text-xs text-muted">
          At "pause" level, the system stops all sending automatically (from {data.bounce.minSample}{' '}
          sends with a result).
        </div>
      </Card>
      <WarmupCard data={data} />
      <StatusList data={data} />
    </>
  );
}

/**
 * "Sending health" on every logged-in page: one status line at the top of the content
 * (verdict, bounce, complaints, warm-up, queue) with a Details sheet holding everything the
 * old right-hand rail showed, and the kill switch as a red text button. A stopped sender
 * keeps its full card (with the resume action) under the line, since it needs action.
 */
export function HealthRail() {
  const { data, isLoading } = useHealthSummary();
  const [detailsOpen, setDetailsOpen] = useState(false);

  if (isLoading || !data) {
    return (
      <div
        role="region"
        aria-label="Sending health"
        className="rounded-[11px] border border-border bg-card px-3.5 py-2.5 text-[13px] text-muted"
      >
        Loading health data…
      </div>
    );
  }

  const status = overallStatus(data);
  const engaged = data.killSwitch.engaged;
  const queue = data.queue.queued + data.queue.retryable;

  return (
    <>
      <div
        role="region"
        aria-label="Sending health"
        className="flex flex-wrap items-center gap-x-[18px] gap-y-1.5 rounded-[11px] border border-border bg-card px-3.5 py-2.5 text-[13px]"
      >
        <span
          className={cn(
            'flex items-center gap-1.5 font-medium',
            status.tone === 'bad' && 'text-danger-text',
            status.tone === 'warn' && 'text-warning-text',
            (status.tone === 'ok' || status.tone === 'early') && 'text-muted',
          )}
        >
          <span aria-hidden="true" className={cn('h-2 w-2 rounded-full', DOT[status.tone])} />
          {status.text}
        </span>
        <Stat label="Bounce">{formatPercent(data.bounce.rate)}</Stat>
        <Stat label="Complaints">{formatPercent(data.complaint.rate)}</Stat>
        {data.warmup ? <Stat label="Warm-up">{warmupProgress(data.warmup).label}</Stat> : null}
        <Stat label="Queue">{formatNumber(queue)}</Stat>
        <span className="flex-grow" />
        <button
          type="button"
          onClick={() => setDetailsOpen(true)}
          className="rounded-md px-1.5 py-1 -mx-1.5 text-[13px] font-semibold text-link hover:bg-accent-soft"
        >
          Details
        </button>
        {engaged ? <ResumeControl /> : <KillSwitchDialog compact />}
      </div>
      <SenderStatusCard senderPaused={data.senderPaused} killSwitchEngaged={engaged} />
      <Sheet open={detailsOpen} onOpenChange={setDetailsOpen}>
        <SheetContent
          eyebrow="Sending"
          title="Sending health"
          description="Rates over the last 7 days, warm-up and system checks."
        >
          <SheetBody>
            <DetailsBody data={data} />
          </SheetBody>
        </SheetContent>
      </Sheet>
    </>
  );
}
